import React from 'react'
import 'project/polyfill'
import { ItemListElements } from 'next-seo/lib/types'

import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import { Utils } from 'common/utils'
import Strings from 'project/localisation'
import { getPlans } from 'common/planData'
import { PlanSlug } from 'common/planSlugs'
import { createClient } from '../prismicio'
import { TPlanSummary } from 'common/cms-types'
import HoxtonMixLogo from 'components/icons/HoxtonMixLogo'
import { generateSrcSet } from 'project/image-proxy-web'
import useWindowSize from 'components/util/useWindowSize'
import { I18NConfig } from 'next/dist/server/config-shared'
import ContactUsCard from 'components/cards/new/ContactUsCard'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import PartnerHeroBanner from 'components/hero/PartnerHeroBanner'
import { contactUsStrings } from 'common/strings/contact-us-strings'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import LondonVOPlusHeroCard from 'components/cards/new/LondonVOPlusHeroCard'

export type PageType = {
  plans: TPlanSummary[]
}

const Page: NextPageWithLayout<PageType> = ({ plans }) => {
  const seoProps: SeoContainerProps['seoProps'] = {
    description: contactUsStrings.seoContactUsDescription,
    openGraph: {
      url: 'https://www.hoxtonmix.com/contact-us/',
    },
    title: contactUsStrings.seoContactUsTitle,
  }
  const breadcrumb: ItemListElements[] = [
    homeBreadcrumb()[0],
    {
      item: 'https://www.hoxtonmix.com/contact-us/',
      name: `${Strings.contactUs}`,
      position: 2,
    },
  ]
  const size = useWindowSize()

  return (
    <SeoContainer
      alternate={[
        { href: '/au/contact-us', lang: 'en-au' },
        { href: '/contact-us', lang: 'en-gb' },
      ]}
      breadcrumb={breadcrumb}
      seoProps={seoProps}
    >
      <main className='overflow-x-hidden new-design d-flex flex-column'>
        <PartnerHeroBanner
          partnershipLogo={
            <HoxtonMixLogo
              className='mb-6 mb-lg-4 max-w-100'
              width={774}
              height={92}
            />
          }
          title={contactUsStrings.wedLoveToHearFromYou}
          robotImage={{
            height: 1000,
            src: '/images/new/robot-holding-mail.png',
            width: 777,
          }}
          imageContainerWidth={size.isLgOrHigher ? '48%' : '100%'}
          description={
            <div className='lh-114 fs-fluid-18-26'>
              <span>{contactUsStrings.wedLoveToHearFromYouDescription}</span>
              <p
                className='mt-5'
                dangerouslySetInnerHTML={{
                  __html: contactUsStrings.contactInstructions,
                }}
              />
            </div>
          }
        />
        <div className='container d-flex flex-column'>
          <div className='d-flex flex-column justify-content-center align-items-center'>
            <img
              {...generateSrcSet('/images/new/oldstreet-tube.svg', 183, 152)}
              loading='lazy'
              alt='contact us'
              className='img-fluid'
            />
            <h2 className='fs-fluid-36-64 flh-105 lh-lg-119 mb-3 mb-lg-0'>
              {contactUsStrings.byTube}
            </h2>
            <span className='fs-fluid-18-28 fw-semibold color-grey5'>
              {contactUsStrings.byTubeDescription}
            </span>
          </div>
          <div className='row gy-5 mt-2 mt-lg-10'>
            <div className='col-12 col-lg-6 '>
              <div className='card py-9 py-lg-10 px-lg-12 px-4'>
                <h2 className='fs-fluid-36-48 flh-105 lh-lg-108 fw-semibold'>
                  {contactUsStrings.fromOldStreet}
                </h2>
                <p
                  className='fs-fluid-18-28 lh-132 lh-lg-114 fw-semibold color-grey5 pt-5 pt-lg-5'
                  dangerouslySetInnerHTML={{
                    __html: contactUsStrings.fromOldStreetDescription,
                  }}
                />
              </div>
            </div>
            <div className='col-12 col-lg-6 '>
              <div className='card py-9 py-lg-10 px-lg-12 px-4'>
                <h2 className='fs-fluid-36-48 flh-105 lh-lg-108 fw-semibold'>
                  {contactUsStrings.fromLiverpoolStreet}
                </h2>
                <p
                  className='fs-fluid-18-28 lh-132 lh-lg-114 fw-semibold color-grey5 pt-5 pt-lg-5'
                  dangerouslySetInnerHTML={{
                    __html: contactUsStrings.fromLiverpoolStreetDescription,
                  }}
                />
              </div>
            </div>
          </div>
          <div className='row gy-5 mt-10 mt-lg-22'>
            <div className='col-12 col-lg-6 '>
              <ContactUsCard />
            </div>
            <div className='col-12 col-lg-6'>
              <LondonVOPlusHeroCard
                planSummary={plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!}
              />
            </div>
          </div>
        </div>
      </main>
    </SeoContainer>
  )
}

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    const plans = await getPlans()
    return {
      props: {
        plans,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}
export default Page
